import type { Ref } from 'vue'

import {
  MAX_SAVED_BUILDS,
  UnsupportedActionError,
} from '../../lib/boardInvariants'
import {
  PlayerAddBuildDocument,
  PlayerRemoveBuildDocument,
  PlayerSetActiveBuildDocument,
  PlayerSetBuildDocument,
  GuildBuildAddDocument,
  GuildBuildSetDocument,
  GuildBuildRemoveDocument,
  GuildBuildAssignDocument,
  GuildBuildUnassignDocument,
  GuildBuildSetActiveDocument,
} from '@/shared/api/gql/graphql'
import type { Player, RaidStageId } from '../../types'

export interface PlayerBuildsActionsCtx {
  readonly activeDiscordGuildId: Readonly<Ref<string>>
  readonly error: Ref<string | null>
  readonly players: Ref<ReadonlyArray<Player>>
  readonly activeStageId: Ref<RaidStageId | null>
  readonly run: <T>(
    mutation: unknown,
    variables: Record<string, unknown>,
    done?: (result: T) => void,
  ) => Promise<void>
}

export interface PlayerBuildsActionsApi {
  readonly addBuild: (id: string, weapons: ReadonlyArray<string>) => void
  readonly removeBuild: (id: string, index: number) => void
  readonly setBuild: (
    id: string,
    index: number,
    weapons: ReadonlyArray<string>,
  ) => Promise<void>
  readonly setActiveBuild: (id: string, index: number) => void
  // Guild library: ops target the guild's library, not a specific player.
  readonly addGuildBuild: (weapons: ReadonlyArray<string>) => void
  readonly setGuildBuild: (buildId: string, weapons: ReadonlyArray<string>) => Promise<void>
  readonly removeGuildBuild: (buildId: string) => void
  // M:M assignment side.
  readonly assignGuildBuild: (playerId: string, buildId: string) => void
  readonly unassignGuildBuild: (playerId: string, buildId: string) => void
  // Activating a build for a player — requires the player is already assigned.
  readonly setActiveGuildBuild: (playerId: string, buildId: string) => void
  readonly clearBuilds: (id: string) => Promise<void>
}

/**
 * Pure helper — a build slot requires exactly two non-empty, mutually distinct
 * weapon slugs. Shared between `addBuild`, `addGuildBuild`, `setBuild`,
 * `setGuildBuild`, and any future call site (the picker / the popup) so the
 * validation lives in one spot instead of being re-asserted at each entry.
 * Returns `false` for arrays whose length is not 2, blank or whitespace-only
 * slugs, and identical pairs.
 */
function isDistinctWeaponPair(weapons: ReadonlyArray<string>): boolean {
  if (weapons.length !== 2) return false
  const [first, second] = weapons
  return first !== undefined
    && second !== undefined
    && first.trim().length > 0
    && second.trim().length > 0
    && first !== second
}

export function usePlayerBuildsActions(
  ctx: PlayerBuildsActionsCtx,
): PlayerBuildsActionsApi {
  function addBuild(id: string, weapons: ReadonlyArray<string>): void {
    if (!isDistinctWeaponPair(weapons)) {
      ctx.error.value = 'Personal builds require two distinct weapons.'
      return
    }
    if ((ctx.players.value.find((p) => p.id === id)?.builds.length ?? 0) >= MAX_SAVED_BUILDS) {
      ctx.error.value = `Build slot full (${MAX_SAVED_BUILDS}).`
      return
    }
    void ctx.run(PlayerAddBuildDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      playerId: Number(id),
      weapons: weapons.slice(0, 2),
      stageId:
        ctx.activeStageId.value === null ? null : Number(ctx.activeStageId.value),
    })
  }

  function removeBuild(id: string, index: number): void {
    void ctx.run(PlayerRemoveBuildDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      playerId: Number(id),
      index,
    })
  }

  function setBuild(
    id: string,
    index: number,
    weapons: ReadonlyArray<string>,
  ): Promise<void> {
    if (!isDistinctWeaponPair(weapons)) {
      const err = new UnsupportedActionError('setBuild requires two distinct weapons')
      ctx.error.value = err.message
      return Promise.reject(err)
    }
    return ctx.run(PlayerSetBuildDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      playerId: Number(id),
      index,
      weapons,
    })
  }

  function setActiveBuild(id: string, index: number): void {
    void ctx.run(PlayerSetActiveBuildDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      playerId: Number(id),
      index,
    })
  }

  /**
   * In-flight tracking map. Coalesces concurrent `clearBuilds` calls for
   * the same player into a single run so a spam-click doesn't pile up
   * additional executions. Lives at module level — same as the original
   * monolithic store — so two `useGvgPlannerStore()` instances inside the
   * same tab share the dedup map.
   */

  function addGuildBuild(weapons: ReadonlyArray<string>): void {
    if (!isDistinctWeaponPair(weapons)) {
      ctx.error.value = 'Guild builds require two distinct weapons.'
      return
    }
    void ctx.run(GuildBuildAddDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      weapons: weapons.slice(0, 2),
    })
  }

  async function setGuildBuild(
    buildId: string,
    weapons: ReadonlyArray<string>,
  ): Promise<void> {
    if (!isDistinctWeaponPair(weapons)) {
      const err = new UnsupportedActionError('setGuildBuild requires two distinct weapons')
      ctx.error.value = err.message
      return Promise.reject(err)
    }
    return ctx.run(GuildBuildSetDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      buildId: Number(buildId),
      weapons,
    })
  }

  function removeGuildBuild(buildId: string): void {
    void ctx.run(GuildBuildRemoveDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      buildId: Number(buildId),
    })
  }

  function assignGuildBuild(playerId: string, buildId: string): void {
    void ctx.run(GuildBuildAssignDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      buildId: Number(buildId),
      playerId: Number(playerId),
    })
  }

  function unassignGuildBuild(playerId: string, buildId: string): void {
    void ctx.run(GuildBuildUnassignDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      buildId: Number(buildId),
      playerId: Number(playerId),
    })
  }

  function setActiveGuildBuild(playerId: string, buildId: string): void {
    void ctx.run(GuildBuildSetActiveDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      buildId: Number(buildId),
      playerId: Number(playerId),
    })
  }
  const clearBuildsInFlight = new Map<string, Promise<void>>()

  async function clearBuilds(id: string): Promise<void> {
    const existing = clearBuildsInFlight.get(id)
    if (existing !== undefined) return existing
    const p = ctx.players.value.find((x) => x.id === id)
    if (!p) return Promise.resolve()
    const indices: ReadonlyArray<number> = p.builds.map((_, i) => i)
    const executeClear = async (): Promise<void> => {
      try {
        // Iterate from the end so the indices we recorded stay valid
        // as each mutation removes one build from the player's list.
        for (let i = indices.length - 1; i >= 0; i -= 1) {
          await ctx.run(PlayerRemoveBuildDocument, {
            guildId: ctx.activeDiscordGuildId.value,
            playerId: Number(id),
            index: indices[i] as number,
          })
        }
      } finally {
        clearBuildsInFlight.delete(id)
      }
    }
    const promise = executeClear()
    clearBuildsInFlight.set(id, promise)
    return promise
  }

  return {
    addBuild,
    removeBuild,
    setBuild,
    setActiveBuild,
    addGuildBuild,
    setGuildBuild,
    removeGuildBuild,
    assignGuildBuild,
    unassignGuildBuild,
    setActiveGuildBuild,
    clearBuilds,
  }
}
